import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import {
  packagingSuggestion,
  applyPackagingSuggestion,
  productValues,
  defineQuantityPackaging,
} from "./productPackaging";
const Field = ({ label, children }) => (
  <label className="purchasing-field">
    <span>{label}</span>
    {children}
  </label>
);

export default function ProductEditor({
  product,
  savedProduct,
  duties,
  busy,
  error,
  notice,
  onChange,
  onClose,
  onSave,
  onConfirmUnit,
}) {
  const [sourceUnit, setSourceUnit] = useState("");
  const [quantityPackage, setQuantityPackage] = useState("");
  useEffect(
    () => setQuantityPackage(""),
    [product?._id, product?.name, product?.specification]
  );
  useEffect(() => {
    setSourceUnit(product?.qbSource?.unit || "");
  }, [product?._id, product?.qbSource?.unit]);
  if (!product) return null;
  const conversions = product.conversions || [],
    suggestion = packagingSuggestion(product);
  const availableUnits = [
    ...new Set(
      [product.baseUnit, ...conversions.map((c) => c.unit)].filter(Boolean)
    ),
  ];
  const currencyConfirmation = (product.history || [])
    .slice()
    .reverse()
    .find(
      (h) =>
        h.action === "currency_confirmed" &&
        h.detail?.currency === product.qbSource?.currency
    );
  const dirty =
    !savedProduct ||
    JSON.stringify(productValues(product)) !==
      JSON.stringify(productValues(savedProduct));
  const change = (key, value) => onChange({ ...product, [key]: value });
  return (
    <Dialog
      header={
        product._id ? `Editar producto — ${product.code}` : "Crear producto"
      }
      contentClassName="purchasing"
      visible
      modal
      blockScroll
      onHide={() => {
        if (!busy) onClose();
      }}
      closable={!busy}
      closeOnEscape={!busy}
      style={{ width: "min(960px, 96vw)" }}
    >
      <section aria-label="Editor de producto">
        <p>{product.name}</p>
        {error && (
          <p role="alert" className="purchasing-error">
            {error}
          </p>
        )}
        {notice && (
          <p role="status" className="purchasing-notice">
            {notice}
          </p>
        )}
        {suggestion && (
          <div
            className="purchasing-evidence"
            aria-label="Sugerencia de empaque"
          >
            {suggestion.status === "ambiguous" ? (
              <>
                <p>
                  Las descripciones contienen empaques múltiples o distintos.
                  Revise el contenido de cada empaque.
                </p>
                {suggestion.candidates.map((c) => (
                  <p key={c.unit + c.factorToBase}>{c.evidence}</p>
                ))}
              </>
            ) : ["quantity_only", "price_basis"].includes(suggestion.status) ? (
              <>
                <p>
                  {suggestion.status === "price_basis"
                    ? "Base de precio detectada"
                    : "Cantidad detectada"}
                  : {suggestion.factorToBase}{" "}
                  {suggestion.baseUnit === "unidad"
                    ? "unidades"
                    : suggestion.baseUnit}
                  .
                </p>
                <p>
                  Evidencia: <strong>{suggestion.evidence}</strong>
                </p>
                {suggestion.status === "quantity_only" ? (
                  <>
                    <p>
                      La descripción no identifica el empaque. Confírmelo antes
                      de agregar una conversión.
                    </p>
                    <Field label="Empaque de la cantidad detectada">
                      <select
                        disabled={busy}
                        value={quantityPackage}
                        onChange={(e) => setQuantityPackage(e.target.value)}
                      >
                        <option value="">Por confirmar</option>
                        {[
                          "caja",
                          "cartón",
                          "bulto",
                          "paquete",
                          "rollo",
                          "unidad comercial",
                        ].map((u) => (
                          <option key={u} value={u}>
                            {u}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <button
                      type="button"
                      disabled={
                        busy ||
                        !quantityPackage ||
                        defineQuantityPackaging(
                          product,
                          suggestion,
                          quantityPackage
                        ) === product
                      }
                      onClick={() =>
                        onChange(
                          defineQuantityPackaging(
                            product,
                            suggestion,
                            quantityPackage
                          )
                        )
                      }
                    >
                      Usar cantidad y empaque revisados
                    </button>
                    {quantityPackage &&
                      defineQuantityPackaging(
                        product,
                        suggestion,
                        quantityPackage
                      ) === product && (
                        <p>
                          Revise la unidad base y las conversiones guardadas; la
                          selección ya existe o difiere de estos datos.
                        </p>
                      )}
                  </>
                ) : (
                  <p>
                    Esta cantidad es la base del precio, no identifica un
                    empaque físico.
                  </p>
                )}
              </>
            ) : (
              <>
                {(suggestion.candidates || [suggestion]).map((c) => (
                  <div key={c.unit + c.factorToBase}>
                    <p>
                      Empaque detectado: 1 {c.unit} = {c.factorToBase}{" "}
                      {(c.baseUnit || suggestion.baseUnit) === "unidad"
                        ? "unidades"
                        : c.baseUnit || suggestion.baseUnit}
                      .
                    </p>
                    <p>
                      Evidencia: <strong>{c.evidence}</strong>
                    </p>
                  </div>
                ))}
                {suggestion.status === "suggested" && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      onChange(applyPackagingSuggestion(product, suggestion))
                    }
                  >
                    Usar empaque detectado
                  </button>
                )}
                {suggestion.status === "present" && (
                  <p>La conversión guardada coincide con esta descripción.</p>
                )}
                {["conflict", "incompatible"].includes(suggestion.status) && (
                  <p>
                    La sugerencia difiere de la unidad o conversión registrada;
                    revísela antes de modificarla.
                  </p>
                )}
              </>
            )}
            <p>
              Las conversiones se guardan después de revisar y guardar el
              producto. La unidad de la cantidad facturada se confirma por
              separado.
            </p>
          </div>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSave(product);
          }}
        >
          <div className="purchasing-fields">
            {[
              ["code", "Código"],
              ["name", "Nombre"],
              ["specification", "Especificación"],
              ["category", "Categoría"],
              ["baseUnit", "Unidad base"],
              ["hsCode", "Código arancelario"],
            ].map(([key, label]) => (
              <Field key={key} label={label}>
                <input
                  disabled={busy}
                  required={["code", "name", "baseUnit"].includes(key)}
                  value={product[key] || ""}
                  onChange={(e) => change(key, e.target.value)}
                />
              </Field>
            ))}
            {["weightKgPerBase", "volumeM3PerBase"].map((key) => (
              <Field
                key={key}
                label={
                  key === "weightKgPerBase"
                    ? "Peso kg por unidad base"
                    : "Volumen m³ por unidad base"
                }
              >
                <input
                  disabled={busy}
                  type="number"
                  step="any"
                  min="0"
                  value={product[key] ?? ""}
                  onChange={(e) => change(key, e.target.value || null)}
                />
              </Field>
            ))}
            <Field label="Regla de arancel">
              <select
                disabled={busy}
                value={product.dutyRule || ""}
                onChange={(e) => change("dutyRule", e.target.value || null)}
              >
                <option value="">Sin confirmar</option>
                {duties.map((d) => (
                  <option key={d._id} value={d._id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Documentos exigidos (separados por coma)">
              <input
                disabled={busy}
                value={(product.requiredDocTypes || []).join(",")}
                onChange={(e) =>
                  change(
                    "requiredDocTypes",
                    e.target.value
                      .split(",")
                      .map((v) => v.trim())
                      .filter(Boolean)
                  )
                }
              />
            </Field>
            <Field label="Estado">
              <select
                disabled={busy}
                value={product.status || "active"}
                onChange={(e) => change("status", e.target.value)}
              >
                <option value="active">Activo</option>
                <option value="archived">Archivado</option>
              </select>
            </Field>
          </div>
          {product.qbSource?.entityId && (
            <details>
              <summary>Referencia de QuickBooks al importar</summary>
              <p>
                SKU: {product.qbSource.sku || "Sin registrar"} · Costo
                registrado: {product.qbSource.purchaseCost ?? "Sin registrar"} ·
                Precio de venta:{" "}
                {product.qbSource.salesPrice ?? "Sin registrar"} · Moneda:{" "}
                {product.qbSource.currency || "Sin confirmar"}
              </p>
              {currencyConfirmation && (
                <p>
                  Moneda confirmada para Compras: {product.qbSource.currency}.
                  Fuente:{" "}
                  {currencyConfirmation.detail.basis ===
                  "administrator_declared_usd"
                    ? "confirmación del administrador"
                    : "moneda explícita de QuickBooks"}
                  .
                </p>
              )}
              <p>
                Descripción de compra:{" "}
                {product.qbSource.purchaseDescription || "Sin registrar"}
              </p>
              <p>
                Descripción de venta:{" "}
                {product.qbSource.salesDescription || "Sin registrar"}
              </p>
              <p>
                Existencias registradas:{" "}
                {product.qbSource.quantityOnHand ?? "Sin registrar"} ·
                Sincronizado:{" "}
                {product.qbSource.syncedAt
                  ? new Date(product.qbSource.syncedAt).toLocaleString()
                  : "Fecha no disponible"}
              </p>
              <p>
                Referencia conservada al importar. Consulte la vista previa de
                QuickBooks para ver los datos sincronizados más recientes.
              </p>
            </details>
          )}
          {conversions.map((c, i) => (
            <div className="purchasing-fields" key={i}>
              <Field label="Unidad de empaque">
                <input
                  disabled={busy}
                  required
                  value={c.unit}
                  onChange={(e) =>
                    change(
                      "conversions",
                      conversions.map((v, n) =>
                        n === i ? { ...v, unit: e.target.value } : v
                      )
                    )
                  }
                />
              </Field>
              <Field label="Cantidad de unidades base">
                <input
                  disabled={busy}
                  required
                  type="number"
                  min="0.00000001"
                  step="any"
                  value={c.factorToBase}
                  onChange={(e) =>
                    change(
                      "conversions",
                      conversions.map((v, n) =>
                        n === i ? { ...v, factorToBase: e.target.value } : v
                      )
                    )
                  }
                />
              </Field>
              <button
                disabled={busy}
                type="button"
                onClick={() =>
                  change(
                    "conversions",
                    conversions.filter((_, n) => n !== i)
                  )
                }
              >
                Eliminar conversión
              </button>
            </div>
          ))}
          <button
            disabled={busy}
            type="button"
            onClick={() =>
              change("conversions", [
                ...conversions,
                { unit: "", factorToBase: "", label: "" },
              ])
            }
          >
            Agregar conversión
          </button>
          <button disabled={busy} type="submit">
            Guardar producto
          </button>
        </form>
        {product._id && product.qbSource?.entityId && (
          <div className="purchasing-evidence">
            <p>
              Guarde primero la unidad base y las conversiones. Luego confirme
              si la cantidad de la factura está expresada en unidades
              individuales o en empaques.
            </p>
            <Field label="Unidad de la cantidad en QuickBooks">
              <select
                disabled={busy}
                value={availableUnits.includes(sourceUnit) ? sourceUnit : ""}
                onChange={(e) => setSourceUnit(e.target.value)}
              >
                <option value="">Sin confirmar</option>
                {availableUnits.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </select>
            </Field>
            {dirty && <p>Hay cambios sin guardar.</p>}
            <button
              type="button"
              disabled={
                busy ||
                dirty ||
                !savedProduct?.baseUnit ||
                !availableUnits.includes(sourceUnit)
              }
              onClick={() => onConfirmUnit(product, sourceUnit)}
            >
              Confirmar unidad de QuickBooks
            </button>
          </div>
        )}
        <button disabled={busy} type="button" onClick={onClose}>
          Cerrar editor
        </button>
      </section>
    </Dialog>
  );
}
